class QuickCallback extends HTMLElement {
  constructor() {
    super();

    this.sheetUrl = this.dataset.sheetUrl;

    this.triggerBtn = this.querySelector(".qc-trigger-btn");
    this.modal = this.querySelector(".qc-modal");
    this.closeBtn = this.querySelector(".qc-modal-close");
    this.overlay = this.querySelector(".qc-modal-overlay");

    this.form = this.querySelector(".qc-form");
    this.msg = this.querySelector(".qc-form-msg");
    this.submitBtn = this.querySelector(".qc-submit-btn");
    this.spinner = this.querySelector(".qc-spinner");
    this.btnText = this.querySelector(".qc-submit-text");

    this.nameInput = this.querySelector("#qc-name");
    this.phoneInput = this.querySelector("#qc-phone");

    // Individual validation messages
    this.nameError = this.querySelector(".qc-name-error");
    this.phoneError = this.querySelector(".qc-phone-error");

    // Track whether user interacted with fields
    this.nameTouched = false;
    this.phoneTouched = false;
    // Track in-flight submission to prevent double-fire
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.triggerBtn?.addEventListener("click", () => this.open());
    this.closeBtn?.addEventListener("click", () => this.close());
    this.overlay?.addEventListener("click", () => this.close());

    this.form?.addEventListener("submit", (e) => this.handleSubmit(e));

    // NAME
    this.nameInput?.addEventListener("input", () => {
      this.nameTouched = true;

      this.sanitizeNameInput();
      this.validateNameField();
      this.updateSubmitState();
    });

    this.nameInput?.addEventListener("blur", () => {
      this.nameTouched = true;

      this.validateNameField();
      this.updateSubmitState();
    });

    // PHONE
    this.phoneInput?.addEventListener("input", () => {
      this.phoneTouched = true;

      // Allow numbers only
      this.phoneInput.value = this.phoneInput.value.replace(/\D/g, "");

      // Limit to 10 digits
      if (this.phoneInput.value.length > 10) {
        this.phoneInput.value = this.phoneInput.value.slice(0, 10);
      }

      this.validatePhoneField();
      this.updateSubmitState();
    });

    this.phoneInput?.addEventListener("blur", () => {
      this.phoneTouched = true;

      this.validatePhoneField();
      this.updateSubmitState();
    });

    this.updateSubmitState();
  }

  open() {
    this.modal?.removeAttribute("hidden");
    document.body.style.overflow = "hidden";
  }

  close() {
    this.modal?.setAttribute("hidden", "");
    document.body.style.overflow = "";
  }

  sanitizeNameInput() {
    if (!this.nameInput) return;

    const sanitizedValue = this.nameInput.value.replace(/[^A-Za-z\s.'-]/g, "");

    if (this.nameInput.value !== sanitizedValue) {
      this.nameInput.value = sanitizedValue;
    }
  }

  validateName(value) {
    const name = (value || "").trim();

    return (
      name.length >= 2 && /[A-Za-z]/.test(name) && /^[A-Za-z\s.'-]+$/.test(name)
    );
  }

  validatePhone(value) {
    const phone = (value || "").replace(/\D/g, "");

    return /^[6-9]\d{9}$/.test(phone);
  }

  validateNameField() {
    if (!this.nameInput || !this.nameError) return;

    const value = this.nameInput.value.trim();

    // Don't show error if empty and user hasn't interacted
    if (!this.nameTouched) {
      this.clearFieldError(this.nameInput, this.nameError);
      return;
    }

    if (!value) {
      this.showFieldError(
        this.nameInput,
        this.nameError,
        "Please enter your name.",
      );
      return false;
    }

    if (value.length < 2) {
      this.showFieldError(
        this.nameInput,
        this.nameError,
        "Name must contain at least 2 characters.",
      );
      return false;
    }

    if (!this.validateName(value)) {
      this.showFieldError(
        this.nameInput,
        this.nameError,
        "Please enter a valid name using letters only.",
      );
      return false;
    }

    this.clearFieldError(this.nameInput, this.nameError);
    return true;
  }

  validatePhoneField() {
    if (!this.phoneInput || !this.phoneError) return;

    const value = this.phoneInput.value.trim();

    if (!this.phoneTouched) {
      this.clearFieldError(this.phoneInput, this.phoneError);
      return;
    }

    if (!value) {
      this.showFieldError(
        this.phoneInput,
        this.phoneError,
        "Please enter your mobile number.",
      );
      return false;
    }

    if (value.length < 10) {
      this.showFieldError(
        this.phoneInput,
        this.phoneError,
        "Mobile number must be 10 digits.",
      );
      return false;
    }

    if (!/^[6-9]/.test(value)) {
      this.showFieldError(
        this.phoneInput,
        this.phoneError,
        "Mobile number must start with 6, 7, 8 or 9.",
      );
      return false;
    }

    if (!this.validatePhone(value)) {
      this.showFieldError(
        this.phoneInput,
        this.phoneError,
        "Enter a valid 10-digit mobile number.",
      );
      return false;
    }

    this.clearFieldError(this.phoneInput, this.phoneError);
    return true;
  }

  showFieldError(input, errorElement, message) {
    input.classList.add("has-error");
    input.setAttribute("aria-invalid", "true");

    errorElement.textContent = message;
    errorElement.classList.add("is-visible");
  }

  clearFieldError(input, errorElement) {
    input.classList.remove("has-error");
    input.removeAttribute("aria-invalid");

    errorElement.textContent = "";
    errorElement.classList.remove("is-visible");
  }

  updateSubmitState() {
    if (!this.submitBtn) return;

    const nameValue = this.nameInput?.value || "";
    const phoneValue = this.phoneInput?.value || "";

    const isNameValid = this.validateName(nameValue);
    const isPhoneValid = this.validatePhone(phoneValue);

    const isValid = isNameValid && isPhoneValid;

    this.submitBtn.disabled = !isValid;
    this.submitBtn.setAttribute("aria-disabled", String(!isValid));
  }

  showMessage(message, type = "error") {
    if (!this.msg) return;

    this.msg.textContent = message;
    this.msg.className = `qc-form-msg ${type}`;
  }

  clearMessage() {
    if (!this.msg) return;

    this.msg.textContent = "";
    this.msg.className = "qc-form-msg";
  }

  fireConversions() {
    // Google Ads conversion
    if (typeof gtag === "function") {
      try {
        gtag("event", "conversion", {
          send_to: "AW-386560685/I7bZCKeW2-gcEK3lqbgB",
          value: 1.0,
          currency: "INR",
        });
      } catch (err) {
        /* gtag not ready — ignore */
      }
    }

    // Meta / Facebook conversion
    if (typeof fbq === "function") {
      try {
        fbq("track", "CompleteRegistration");
      } catch (err) {
        /* fbq not ready — ignore */
      }
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    // Hard guard: ignore any submit while one is already in flight
    if (this.isSubmitting) return;

    // Mark both fields as touched
    this.nameTouched = true;
    this.phoneTouched = true;

    // Validate fields
    const isNameValid = this.validateNameField();
    const isPhoneValid = this.validatePhoneField();

    this.updateSubmitState();

    // Stop submission if invalid
    if (!isNameValid || !isPhoneValid) {
      this.clearMessage();

      if (!isNameValid) {
        this.nameInput?.focus();
      } else if (!isPhoneValid) {
        this.phoneInput?.focus();
      }

      return;
    }

    // Lock submission
    this.isSubmitting = true;

    // Loading state
    this.submitBtn.disabled = true;
    this.btnText?.classList.add("loading");
    this.spinner?.removeAttribute("hidden");

    this.clearMessage();

    const formData = new FormData(this.form);

    // --- UTM / Zoho tracking (same pattern as the HTO form) ---
    let utmSource = "";
    let zohoData = null;
    try {
      utmSource = sessionStorage.getItem("utmData") || "";
      zohoData = JSON.parse(sessionStorage.getItem("zohoUtmData"));
    } catch (err) {
      utmSource = "";
      zohoData = null;
    }

    formData.append("Source", utmSource);

    if (zohoData) {
      formData.append("UTM Adset", zohoData.utm_adset || "");
      formData.append("UTM Ad", zohoData.utm_ad || "");
      formData.append("UTM Campaign", zohoData.utm_campaign || "");
      formData.append("GCLID", zohoData.gclid || "");
      formData.append("FBCLID", zohoData.fbclid || "");
    }

    try {
      const response = await fetch(this.sheetUrl, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.result === "success" || response.ok) {
        this.showMessage(
          "Request submitted successfully! We will call you shortly.",
          "success",
        );

        this.fireConversions();   // ← add this line      
        this.form.reset();

        this.nameTouched = false;
        this.phoneTouched = false;

        this.clearFieldError(this.nameInput, this.nameError);
        this.clearFieldError(this.phoneInput, this.phoneError);

        setTimeout(() => {
          this.close();
          this.clearMessage();
        }, 3000);
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      console.error("Error submitting callback form:", error);

      this.showMessage("Something went wrong. Please try again.", "error");
    } finally {
      this.isSubmitting = false;

      this.spinner?.setAttribute("hidden", "");
      this.btnText?.classList.remove("loading");

      this.updateSubmitState();
    }
  }
}

if (!customElements.get("quick-callback")) {
  customElements.define("quick-callback", QuickCallback);
}
